/**
 * M04 Skill/Tool fixture · S3 Day11
 */
import type {
	OAuthHealthResponse,
	SkillDecisionPreviewResponse,
	SkillListItem,
	SkillPromptResponse,
	SkillSandboxResponse,
	SkillToggleResponse,
	SkillsListResponse,
} from "@/lib/api/skills";

import { registerFixture } from "./registry";
import type { MockResponse } from "./types";

type SkillFixtureRow = readonly [
	SkillListItem["skill_id"],
	SkillListItem["name"],
	SkillListItem["description"],
	SkillListItem["skill_class"],
	SkillListItem["risk_level"],
	SkillListItem["tool_id"],
	SkillListItem["requires_dry_run"],
	SkillListItem["requires_confirmation"],
	SkillListItem["pre_fetchable"],
	SkillListItem["pre_fetch_trigger_slots"],
];

const SKILL_ROWS: SkillFixtureRow[] = [
	[
		"SK01",
		"订单列表定位",
		"按订单号、订单名或邮箱定位候选订单。",
		"read-only",
		"R0",
		"query_order",
		false,
		false,
		true,
		["order_id", "email", "customer_email"],
	],
	[
		"SK02",
		"订单详情聚合",
		"读取订单履约、物流与退款/取消辅证。",
		"read-only",
		"R0",
		"query_shipping_status",
		false,
		false,
		true,
		["order_id"],
	],
	[
		"SK03",
		"商品详情获取",
		"读取商品详情、变体选项和售前对比所需事实。",
		"read-only",
		"R0",
		"query_product_variants",
		false,
		false,
		true,
		["product_id", "product_name"],
	],
	[
		"SK04",
		"商品推荐",
		"基于 queryKeys 返回商品推荐候选，服务找同款、库存替代和售前推荐。",
		"read-only",
		"R0",
		"product_recommendations",
		false,
		false,
		true,
		["queryKeys"],
	],
	[
		"SK05",
		"知识检索",
		"检索政策、FAQ、商品知识或运营知识库内容。",
		"read-only",
		"R0",
		"knowledge_search",
		false,
		false,
		true,
		[],
	],
	[
		"SK06",
		"优惠规则查询",
		"查询优惠码、折扣规则与适用约束。",
		"read-only",
		"R0",
		"query_discount_code",
		false,
		false,
		true,
		["discount_code"],
	],
	[
		"SK07",
		"取消订单",
		"取消订单写操作，需确认、dry-run 和质量门保护。",
		"write",
		"R2",
		"cancel_order",
		true,
		true,
		false,
		[],
	],
	[
		"SK08",
		"退货创建",
		"创建退货写操作，需确认、dry-run 和质量门保护。",
		"write",
		"R2",
		"create_return",
		true,
		true,
		false,
		[],
	],
	[
		"SK09",
		"退款执行",
		"执行退款写操作，需确认、dry-run 和质量门保护。",
		"write",
		"R2",
		"refund_order",
		true,
		true,
		false,
		[],
	],
	[
		"SK10",
		"修改地址",
		"修改收货地址写操作，需确认、dry-run 和质量门保护。",
		"write",
		"R2",
		"update_shipping_address",
		true,
		true,
		false,
		[],
	],
	["SK11", "转人工", "转人工处理，保留上下文摘要。", "write", "R1", null, false, false, false, []],
];

const SKILLS: SkillListItem[] = SKILL_ROWS.map(
	([
		skill_id,
		name,
		description,
		skill_class,
		risk_level,
		tool_id,
		requires_dry_run,
		requires_confirmation,
		pre_fetchable,
		slots,
	]) => ({
		skill_id,
		name,
		description,
		category: skill_id === "SK05" ? "rag_qa" : skill_id === "SK11" ? "handoff" : "shopify_admin",
		skill_class,
		risk_level,
		tool_id,
		requires_dry_run,
		requires_confirmation,
		pre_fetchable,
		pre_fetch_trigger_slots: slots,
		trigger_mode: "any",
		enabled: true,
		health_status: "not_checked",
		updated_by: null,
		updated_at: null,
	}),
);

const SKILL_PROMPTS = new Map<string, SkillPromptResponse>();

function defaultSkillPrompt(skill: SkillListItem): SkillPromptResponse {
	return {
		skill_id: skill.skill_id,
		source: "default",
		planner_mode: "rule_guarded_prompt",
		prompt_template: [
			`# ${skill.skill_id} ${skill.name}`,
			"",
			"## 业务目标",
			skill.description,
			"",
			"## 参数抽取要求",
			"优先从当前客户问题、上文摘要和已抽取槽位中生成结构化 args。",
			"",
			"## 禁止事项",
			skill.skill_class === "write"
				? "只能生成 plan-only dry-run，不得承诺真实写入已完成。"
				: "只能读取事实，不得修改 Shopify 数据。",
		].join("\n"),
		updated_by: null,
		notes: null,
		updated_at: null,
	};
}

function skillPrompt(skillId: string): SkillPromptResponse | null {
	const skill = SKILLS.find((item) => item.skill_id === skillId);
	if (!skill) return null;
	return SKILL_PROMPTS.get(skillId) ?? defaultSkillPrompt(skill);
}

function listResponse(): SkillsListResponse {
	const enabled = SKILLS.filter((item) => item.enabled).length;
	return {
		items: SKILLS,
		total: SKILLS.length,
		enabled_count: enabled,
		disabled_count: SKILLS.length - enabled,
	};
}

export function registerM04Fixtures(): void {
	registerFixture<SkillsListResponse>({
		pathname: "/admin/v1/skills/skills",
		methods: ["GET"],
		resolve: () => ({ data: listResponse() }),
	});

	registerFixture<SkillToggleResponse | { detail: string }>({
		pathname: "/admin/v1/skills/skills/:skill_id/toggle",
		methods: ["POST"],
		resolve: (ctx): MockResponse<SkillToggleResponse | { detail: string }> => {
			const skill = SKILLS.find((item) => item.skill_id === ctx.params.skill_id);
			if (!skill) return { status: 404, data: { detail: "mock skill not found" } };
			const body = (ctx.body ?? {}) as { enabled?: boolean; updated_by?: string; reason?: string };
			skill.enabled = Boolean(body.enabled);
			skill.updated_by = body.updated_by ?? "mock-admin";
			skill.updated_at = new Date().toISOString();
			return {
				data: {
					skill_id: skill.skill_id,
					enabled: skill.enabled,
					updated_by: skill.updated_by,
					reason: body.reason ?? null,
					updated_at: skill.updated_at,
				},
			};
		},
	});

	registerFixture<SkillPromptResponse | { detail: string }>({
		pathname: "/admin/v1/skills/skills/:skill_id/prompt",
		methods: ["GET"],
		resolve: (ctx): MockResponse<SkillPromptResponse | { detail: string }> => {
			const prompt = skillPrompt(ctx.params.skill_id ?? "");
			if (!prompt) return { status: 404, data: { detail: "mock skill not found" } };
			return { data: prompt };
		},
	});

	registerFixture<SkillPromptResponse | { detail: string }>({
		pathname: "/admin/v1/skills/skills/:skill_id/prompt",
		methods: ["PATCH"],
		resolve: (ctx): MockResponse<SkillPromptResponse | { detail: string }> => {
			const skillId = ctx.params.skill_id ?? "";
			const skill = SKILLS.find((item) => item.skill_id === skillId);
			if (!skill) return { status: 404, data: { detail: "mock skill not found" } };
			const body = (ctx.body ?? {}) as {
				prompt_template?: string;
				updated_by?: string | null;
				notes?: string | null;
			};
			const next: SkillPromptResponse = {
				skill_id: skill.skill_id,
				source: "custom",
				planner_mode: "rule_guarded_prompt",
				prompt_template: body.prompt_template ?? defaultSkillPrompt(skill).prompt_template,
				updated_by: body.updated_by ?? "mock-admin",
				notes: body.notes ?? null,
				updated_at: new Date().toISOString(),
			};
			SKILL_PROMPTS.set(skillId, next);
			return { data: next };
		},
	});

	registerFixture<SkillDecisionPreviewResponse | { detail: string }>({
		pathname: "/admin/v1/skills/skills/:skill_id/decision-preview",
		methods: ["POST"],
		resolve: (ctx) => {
			const skillId = ctx.params.skill_id ?? "";
			const fallbackSkill = SKILLS[0];
			if (!fallbackSkill) return { status: 404, data: { detail: "mock skill not found" } };
			const prompt = skillPrompt(skillId) ?? defaultSkillPrompt(fallbackSkill);
			const body = (ctx.body ?? {}) as {
				refined_query?: string;
				allowed_skills?: string[];
				extracted_slots?: Record<string, unknown>;
			};
			const allowed = body.allowed_skills?.includes(skillId) ?? false;
			const productName = /Gift Card/i.test(body.refined_query ?? "") ? "Gift Card" : undefined;
			return {
				delay_ms: 120,
				data: {
					skill_id: skillId,
					planner_mode: "rule_guarded_prompt",
					prompt,
					decision: {
						skill_id: skillId,
						status: allowed ? "planned" : "blocked",
						run_mode: "shadow",
						decision_source: "rule",
						allowed_by_playbook: allowed,
						selected_tool_id: SKILLS.find((item) => item.skill_id === skillId)?.tool_id ?? null,
						resolved_args: productName
							? [{ name: "product_name", value: productName, source: "rule", confidence: 1 }]
							: [],
						missing_slots: [],
						tool_plan: allowed
							? {
									tool_id: SKILLS.find((item) => item.skill_id === skillId)?.tool_id ?? "mock_tool",
									args: productName
										? { merchant_id: "mer_devcons", product_name: productName }
										: {},
									readiness: "ready",
									missing_args: [],
								}
							: null,
						planner_version: "mock",
					},
				},
			};
		},
	});

	registerFixture<SkillSandboxResponse>({
		pathname: "/admin/v1/skills/skills/:skill_id/sandbox",
		methods: ["POST"],
		resolve: (ctx) => {
			const skillId = ctx.params.skill_id ?? "";
			const isWrite = ["SK07", "SK08", "SK09", "SK10", "SK11"].includes(skillId);
			return {
				delay_ms: 150,
				data: {
					skill_id: skillId,
					executed: !isWrite,
					dry_run_only: isWrite,
					message: isWrite ? "Write Skill sandbox is dry-run only." : "Skill sandbox completed.",
					elapsed_ms: 1,
					result: isWrite
						? null
						: {
								status: "success_complete",
								skill_id: skillId,
								result: { mock: true, slots: ctx.body },
							},
				},
			};
		},
	});

	registerFixture<OAuthHealthResponse>({
		pathname: "/admin/v1/skills/oauth-health",
		methods: ["GET"],
		resolve: (ctx) => {
			// probe=true（接入状态页自动 live probe）→ 模拟连通通过；未探活（m04 初始）→ not_checked。
			const probed = ctx.query.probe === "true" || ctx.query.probe === "1";
			return {
				data: {
					merchant_id: ctx.query.merchant_id ?? "mer_nY0bZmLI",
					shopify_domain: "shenwentao.myshopify.com",
					oauth_state: "active",
					scopes_count: 98,
					access_token_expires_at: new Date(Date.now() + 3600_000).toISOString(),
					shopify_api_status: probed ? "ok" : "not_checked",
					checked_live: probed,
					message: probed
						? "mock Shopify Admin GraphQL probe succeeded"
						: "mock OAuth config loaded",
				},
			};
		},
	});
}
